import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck } from 'lucide-react';
import { authApi } from '../../services/apiService';

interface AuthResponse {
  token: string;
  user: {
    id: string;
    username: string;
    email: string;
    role: string;
  };
}

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [language] = useState<'en' | 'pl'>('pl'); // Default to Polish
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/admin');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Call backend API for authentication
      const response = await authApi.login(credentials);

      if (response.success && response.data) {
        // Store token and user info in localStorage (not sessionStorage)
        const authData = response.data as AuthResponse;
        localStorage.setItem('authToken', authData.token);
        localStorage.setItem('adminUser', JSON.stringify(authData.user));
        
        navigate('/admin');
      } else {
        setError(language === 'en' ? 'Invalid username or password' : 'Nieprawidłowa nazwa użytkownika lub hasło');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(
        language === 'en' 
          ? error.message || 'Failed to connect to server. Please try again.' 
          : error.message || 'Nie udało się połączyć z serwerem. Spróbuj ponownie.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <ShieldCheck className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {language === 'en' ? 'Admin Login' : 'Logowanie Administratora'}
            </h1>
            <p className="text-gray-500">
              {language === 'en' ? 'Sign in to access the admin dashboard' : 'Zaloguj się, aby uzyskać dostęp do panelu administratora'}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'en' ? 'Username' : 'Nazwa użytkownika'}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                required
                autoComplete="username"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                placeholder={language === 'en' ? 'Enter your username' : 'Wprowadź nazwę użytkownika'}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {language === 'en' ? 'Password' : 'Hasło'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                placeholder={language === 'en' ? 'Enter your password' : 'Wprowadź hasło'}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {language === 'en' ? 'Signing in...' : 'Logowanie...'}
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  {language === 'en' ? 'Sign In' : 'Zaloguj się'}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← {language === 'en' ? 'Back to Store' : 'Powrót do sklepu'}
          </button>
        </div>
      </div>
    </div>
  );
};
